import React, { useEffect, useMemo, useRef, useState } from "react";
import { Avatar } from "../../components/ui";
import { Icon } from "../shared/components";
import {
  ensureArray, formatMoney, formatTime, formatBrazilPhone,
} from "../shared/utils";

/* ── Helpers ─────────────────────────────────────────────────── */
const ORIGIN_LABELS = { whatsapp: "WhatsApp", indicacao: "Indicação", site: "Site", instagram: "Instagram", facebook: "Facebook", google: "Google Ads", ligacao: "Ligação", outro: "Outro" };
const STAGE_COLORS  = ["#3B9DFF","#A78BFA","#F5A524","#4ADE80","#25D366","#F75555","#00B4D8","#FF6B35"];

function timeSince(dateStr) {
  if (!dateStr) return "recente";
  const diff = (Date.now() - new Date(dateStr).getTime()) / 1000;
  if (diff < 7200) return "recente";
  if (diff < 86400) return `${Math.round(diff / 3600)}h`;
  return `${Math.round(diff / 86400)}d`;
}

/* ── Score badge ─────────────────────────────────────────────── */
function ScoreBadge({ score, grade, label }) {
  const cls = grade === "A" ? "scoreA" : grade === "B" ? "scoreB" : grade === "C" ? "scoreC" : "scoreD";
  return (
    <div className={`leadScoreBadge ${cls}`}>
      <strong>{score ?? "–"}</strong>
      <span>{grade ? `${grade} · ${label}` : "Não calculado"}</span>
    </div>
  );
}

/* ── Task item ───────────────────────────────────────────────── */
function TaskItem({ task, onToggle, onDelete }) {
  return (
    <div className={`taskItem ${task.done ? "done" : ""} ${task.priority}`}>
      <button className="taskCheck" onClick={() => onToggle(task)} title={task.done ? "Reabrir" : "Concluir"}>
        <Icon name={task.done ? "check" : "square"} size={14} />
      </button>
      <div className="taskBody">
        <strong>{task.title}</strong>
        {task.description && <p>{task.description}</p>}
        {task.due_at && <small className={new Date(task.due_at) < new Date() && !task.done ? "overdue" : ""}>{formatTime(task.due_at)}</small>}
      </div>
      <div className="taskMeta">
        <span className={`taskPriority ${task.priority}`}>{task.priority}</span>
        <button className="ghostButton xs" onClick={() => onDelete(task)}><Icon name="trash" size={12} /></button>
      </div>
    </div>
  );
}

/* ── Novo Lead Modal ─────────────────────────────────────────── */
function NewLeadModal({ contacts, stages, users, api, user, onClose, onSaved, defaultStageId }) {
  const [form, setForm] = useState({
    contact_id: "", stage_id: defaultStageId || stages[0]?.id || "",
    title: "", value: "", responsible_user_id: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose(); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function handle(e) {
    e.preventDefault();
    if (!form.contact_id) { setError("Selecione um contato."); return; }
    setSaving(true); setError("");
    try {
      await api.post("/leads", {
        contact_id: Number(form.contact_id),
        stage_id: form.stage_id ? Number(form.stage_id) : null,
        title: form.title || "Nova oportunidade",
        value: Number(form.value || 0),
        responsible_user_id: form.responsible_user_id ? Number(form.responsible_user_id) : user.id,
      });
      onSaved();
    } catch (err) { setError(err.response?.data?.error || "Erro ao criar lead."); }
    finally { setSaving(false); }
  }

  return (
    <div className="finModal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="finModal-box" role="dialog" style={{ maxWidth: 480 }}>
        <div className="finModal-header">
          <div>
            <div className="finModal-title">Novo lead</div>
            <div className="finModal-subtitle">Adicione uma nova oportunidade ao funil</div>
          </div>
          <button type="button" className="finModal-close" onClick={onClose}><Icon name="close" size={14} /></button>
        </div>
        <form onSubmit={handle} className="finModal-body" style={{ gap: 14 }}>
          <div className="finModal-section">
            <div className="finModal-fieldLabel">Contato <span className="finModal-req">*</span></div>
            <select value={form.contact_id} onChange={(e) => setForm((f) => ({ ...f, contact_id: e.target.value }))} className="finModal-select">
              <option value="">Selecione o contato</option>
              {contacts.map((c) => <option key={c.id} value={c.id}>{c.name} — {c.phone}</option>)}
            </select>
          </div>
          <div className="finModal-section">
            <div className="finModal-fieldLabel">Título da oportunidade</div>
            <input className="finModal-input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Ex: Proposta de plano Premium" />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <div className="finModal-section">
              <div className="finModal-fieldLabel">Valor (R$)</div>
              <input className="finModal-input" type="number" value={form.value} onChange={(e) => setForm((f) => ({ ...f, value: e.target.value }))} placeholder="0,00" />
            </div>
            <div className="finModal-section">
              <div className="finModal-fieldLabel">Etapa</div>
              <select className="finModal-select" value={form.stage_id} onChange={(e) => setForm((f) => ({ ...f, stage_id: e.target.value }))}>
                {stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>
          <div className="finModal-section">
            <div className="finModal-fieldLabel">Responsável</div>
            <select className="finModal-select" value={form.responsible_user_id} onChange={(e) => setForm((f) => ({ ...f, responsible_user_id: e.target.value }))}>
              <option value="">Eu mesmo</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
          {error && <div className="finModal-error"><Icon name="warning" size={13} /> {error}</div>}
          <div className="finModal-footer">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancelar</button>
            <div style={{ flex: 1 }} />
            <button type="submit" className="btn btn-primary" disabled={saving}>
              <Icon name="plus" size={13} />
              {saving ? "Criando…" : "Criar lead"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ── Pendências Modal ────────────────────────────────────────── */
function PendenciasModal({ lead, api, onClose }) {
  const [tasks, setTasks]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [adding, setAdding]     = useState(false);
  const [form, setForm]         = useState({ title: "", due_at: "", priority: "media" });
  const [saving, setSaving]     = useState(false);

  async function load() {
    setLoading(true);
    try { const r = await api.get(`/tasks?lead_id=${lead.id}`); setTasks(ensureArray(r.data)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, [lead.id]);

  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose(); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function createTask(e) {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      await api.post("/tasks", { lead_id: lead.id, title: form.title, due_at: form.due_at || null, priority: form.priority });
      setForm({ title: "", due_at: "", priority: "media" }); setAdding(false); await load();
    } finally { setSaving(false); }
  }

  async function toggleTask(task) {
    try { await api.patch(`/tasks/${task.id}`, { done: !task.done }); await load(); } catch {}
  }
  async function deleteTask(task) {
    try { await api.delete(`/tasks/${task.id}`); await load(); } catch {}
  }

  const pending = tasks.filter((t) => !t.done).length;

  return (
    <div className="finModal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="finModal-box" role="dialog" style={{ maxWidth: 460 }}>
        <div className="finModal-header">
          <div>
            <div className="finModal-title">Pendências — {lead.title || lead.contact_name}</div>
            <div className="finModal-subtitle">{tasks.length} tarefa(s) · {pending} pendente(s)</div>
          </div>
          <button type="button" className="finModal-close" onClick={onClose}><Icon name="close" size={14} /></button>
        </div>
        <div className="finModal-body">
          {loading && <div style={{ textAlign: "center", color: "var(--text-4)", padding: "24px 0" }}>Carregando…</div>}
          {!loading && tasks.length === 0 && !adding && (
            <div style={{ textAlign: "center", color: "var(--text-4)", padding: "24px 0" }}>
              <Icon name="task" size={28} /><br />Nenhuma tarefa criada ainda.
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 300, overflowY: "auto" }}>
            {tasks.map((task) => (
              <TaskItem key={task.id} task={task} onToggle={toggleTask} onDelete={deleteTask} />
            ))}
          </div>
          {adding && (
            <form onSubmit={createTask} className="finModal-section" style={{ marginTop: 12 }}>
              <input className="finModal-input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} placeholder="Título da tarefa" autoFocus required />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 8 }}>
                <div>
                  <div className="finModal-fieldLabel">Prazo</div>
                  <input className="finModal-input" type="datetime-local" value={form.due_at} onChange={(e) => setForm((f) => ({ ...f, due_at: e.target.value }))} />
                </div>
                <div>
                  <div className="finModal-fieldLabel">Prioridade</div>
                  <select className="finModal-select" value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}>
                    <option value="baixa">Baixa</option>
                    <option value="media">Média</option>
                    <option value="alta">Alta</option>
                  </select>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>{saving ? "…" : "Criar"}</button>
                <button type="button" className="btn btn-ghost btn-sm" onClick={() => setAdding(false)}>Cancelar</button>
              </div>
            </form>
          )}
        </div>
        <div className="finModal-footer">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Fechar</button>
          <div style={{ flex: 1 }} />
          <button type="button" className="btn btn-primary" onClick={() => setAdding(true)}>
            <Icon name="plus" size={13} /> Nova tarefa
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Anexos Modal ────────────────────────────────────────────── */
function AnexosModal({ lead, api, onClose, onSaved }) {
  const [notes, setNotes]   = useState(lead.notes || "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved]   = useState(false);

  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose(); }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function handle(e) {
    e.preventDefault(); setSaving(true); setSaved(false);
    try {
      await api.patch(`/leads/${lead.id}`, { notes });
      setSaved(true); onSaved?.();
    } catch {}
    finally { setSaving(false); }
  }

  return (
    <div className="finModal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="finModal-box" role="dialog" style={{ maxWidth: 460 }}>
        <div className="finModal-header">
          <div>
            <div className="finModal-title">Anexos &amp; Anotações</div>
            <div className="finModal-subtitle">{lead.title || lead.contact_name}</div>
          </div>
          <button type="button" className="finModal-close" onClick={onClose}><Icon name="close" size={14} /></button>
        </div>
        <form onSubmit={handle} className="finModal-body">
          <div className="finModal-section">
            <div className="finModal-fieldLabel">Anotações &amp; links importantes</div>
            <textarea
              className="finModal-textarea"
              rows={7}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={"Registre links de documentos, arquivos no Drive, observações da negociação...\n\nEx: Contrato: https://drive.google.com/...\nProposta enviada em 01/05 — aguardando retorno."}
            />
            <div className="finModal-hint">Use links do Google Drive, Dropbox ou qualquer URL de documento.</div>
          </div>
          {saved && <div style={{ color: "var(--brand-500)", fontSize: "var(--text-xs)", fontWeight: 600 }}>✓ Salvo com sucesso!</div>}
        </form>
        <div className="finModal-footer">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Fechar</button>
          <div style={{ flex: 1 }} />
          <button type="button" className="btn btn-primary" onClick={handle} disabled={saving}>
            <Icon name="check" size={13} />
            {saving ? "Salvando…" : "Salvar anotações"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Lead Drawer (detail panel) ──────────────────────────────── */
function LeadDrawer({ lead, stages, users, api, user, onClose, onReload, setActivePage, initialTab }) {
  const [tab, setTab]         = useState(initialTab || "info");
  const [tasks, setTasks]     = useState([]);
  const [scoreData, setScoreData] = useState(null);
  const [scoring, setScoring] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [notice, setNotice]   = useState("");

  const [editForm, setEditForm] = useState({
    origin: lead.origin || "",
    loss_reason: lead.loss_reason || "",
    notes: lead.notes || "",
    follow_up_at: lead.follow_up_at ? lead.follow_up_at.slice(0, 16) : "",
    value: lead.value || "",
    title: lead.title || "",
    responsible_user_id: lead.responsible_user_id || "",
    expected_close_date: lead.expected_close_date ? lead.expected_close_date.slice(0, 10) : "",
  });

  const [taskForm, setTaskForm] = useState({ title: "", description: "", due_at: "", priority: "media" });
  const [addingTask, setAddingTask] = useState(false);

  const selectedStage = stages.find((s) => Number(s.id) === Number(lead.stage_id));

  async function loadTasks() {
    try { const res = await api.get(`/tasks?lead_id=${lead.id}`); setTasks(ensureArray(res.data)); } catch {}
  }
  useEffect(() => { loadTasks(); }, [lead.id]);

  async function calcScore() {
    setScoring(true);
    try { const res = await api.post(`/leads/${lead.id}/score`); setScoreData(res.data); }
    catch (err) { setNotice(err.response?.data?.error || "Erro ao calcular score."); }
    finally { setScoring(false); }
  }

  async function saveInfo(e) {
    e.preventDefault(); setSaving(true); setNotice("");
    try {
      await api.patch(`/leads/${lead.id}`, {
        origin: editForm.origin || null, loss_reason: editForm.loss_reason || null,
        notes: editForm.notes || null, follow_up_at: editForm.follow_up_at || null,
        value: editForm.value !== "" ? Number(editForm.value) : undefined,
        title: editForm.title || undefined,
        responsible_user_id: editForm.responsible_user_id ? Number(editForm.responsible_user_id) : undefined,
        expected_close_date: editForm.expected_close_date || null,
      });
      setNotice("Salvo com sucesso."); await onReload();
    } catch (err) { setNotice(err.response?.data?.error || "Erro ao salvar."); }
    finally { setSaving(false); }
  }

  async function moveLead(stageId) {
    try { await api.patch(`/leads/${lead.id}/stage`, { stage_id: Number(stageId) }); await onReload(); }
    catch (err) { setNotice(err.response?.data?.error || "Erro ao mover."); }
  }

  async function changeStatus(status) {
    try { await api.patch(`/leads/${lead.id}/status`, { status }); await onReload(); if (status !== "aberto") onClose(); }
    catch (err) { setNotice(err.response?.data?.error || "Erro ao alterar status."); }
  }

  async function createTask(e) {
    e.preventDefault();
    if (!taskForm.title.trim()) return;
    try {
      await api.post("/tasks", { lead_id: lead.id, title: taskForm.title, description: taskForm.description || null, due_at: taskForm.due_at || null, priority: taskForm.priority });
      setTaskForm({ title: "", description: "", due_at: "", priority: "media" }); setAddingTask(false); await loadTasks();
    } catch (err) { setNotice(err.response?.data?.error || "Erro ao criar tarefa."); }
  }

  async function toggleTask(task) { try { await api.patch(`/tasks/${task.id}`, { done: !task.done }); await loadTasks(); } catch {} }
  async function deleteTask(task) { try { await api.delete(`/tasks/${task.id}`); await loadTasks(); } catch {} }

  const originOptions = [
    { value: "", label: "Não informado" }, { value: "whatsapp", label: "WhatsApp" },
    { value: "indicacao", label: "Indicação" }, { value: "site", label: "Site" },
    { value: "instagram", label: "Instagram" }, { value: "facebook", label: "Facebook" },
    { value: "google", label: "Google Ads" }, { value: "ligacao", label: "Ligação" },
    { value: "outro", label: "Outro" },
  ];

  return (
    <div className="finModal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="finModal-box leadModal" role="dialog" aria-modal="true">

        {/* ── Header ── */}
        <div className="finModal-header" style={{ alignItems: "flex-start" }}>
          <div className="leadModalHero">
            <Avatar name={lead.contact_name || lead.title} size="md" />
            <div>
              <div className="finModal-title">{lead.title || "Oportunidade"}</div>
              <div className="finModal-subtitle">
                {lead.contact_name || "Sem contato"} · {formatBrazilPhone(lead.contact_phone || "") || selectedStage?.name || "Sem etapa"}
              </div>
              <span className="leadStatusPill" style={{ marginTop: 4, display: "inline-block" }}>{lead.status || "aberto"}</span>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
            <div style={{ textAlign: "right" }}>
              <ScoreBadge score={lead.score || scoreData?.score} grade={scoreData?.grade} label={scoreData?.label} />
              <button className="ghostButton xs" style={{ marginTop: 4 }} onClick={calcScore} disabled={scoring}>
                {scoring ? "…" : "Calcular score"}
              </button>
            </div>
            <button type="button" className="finModal-close" onClick={onClose}><Icon name="close" size={14} /></button>
          </div>
        </div>

        {/* Score factors */}
        {scoreData?.factors && (
          <div className="scoreFactorsList" style={{ padding: "0 20px 4px" }}>
            {scoreData.factors.map((f, i) => (
              <div key={i} className="scoreFactor">
                <span>{f.label}</span>
                <div className="scoreFactorBar"><div style={{ width: `${(f.score / f.max) * 100}%` }} /></div>
                <em>{f.score}/{f.max}</em>
              </div>
            ))}
          </div>
        )}

        {notice && <div className="noticeBox" style={{ margin: "0 20px 8px" }}>{notice}</div>}

        {/* ── Tabs ── */}
        <div className="drawerTabs" style={{ padding: "0 20px", borderBottom: "1px solid var(--border)" }}>
          <button className={tab === "info"  ? "active" : ""} onClick={() => setTab("info")}>Informações</button>
          <button className={tab === "tasks" ? "active" : ""} onClick={() => setTab("tasks")}>
            Tarefas {tasks.length > 0 && <em>{tasks.filter((t) => !t.done).length}</em>}
          </button>
          <button className={tab === "notes" ? "active" : ""} onClick={() => setTab("notes")}>Anotações</button>
        </div>

        {/* ── Body ── */}
        <div className="finModal-body leadModalBody">

          {tab === "info" && (
            <form onSubmit={saveInfo} className="leadEditForm">
              <div className="finModal-section">
                <div className="finModal-fieldLabel">Título da oportunidade</div>
                <input className="finModal-input" value={editForm.title} onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))} placeholder="Ex: Proposta de serviço" />
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div className="finModal-section">
                  <div className="finModal-fieldLabel">Valor (R$)</div>
                  <input className="finModal-input" type="number" value={editForm.value} onChange={(e) => setEditForm((f) => ({ ...f, value: e.target.value }))} placeholder="0,00" />
                </div>
                <div className="finModal-section">
                  <div className="finModal-fieldLabel">Fechamento previsto</div>
                  <input className="finModal-input" type="date" value={editForm.expected_close_date} onChange={(e) => setEditForm((f) => ({ ...f, expected_close_date: e.target.value }))} />
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div className="finModal-section">
                  <div className="finModal-fieldLabel">Origem</div>
                  <select className="finModal-select" value={editForm.origin} onChange={(e) => setEditForm((f) => ({ ...f, origin: e.target.value }))}>
                    {originOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div className="finModal-section">
                  <div className="finModal-fieldLabel">Responsável</div>
                  <select className="finModal-select" value={editForm.responsible_user_id} onChange={(e) => setEditForm((f) => ({ ...f, responsible_user_id: e.target.value }))}>
                    <option value="">Eu mesmo</option>
                    {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
              </div>
              <div className="finModal-section">
                <div className="finModal-fieldLabel">Follow-up agendado</div>
                <input className="finModal-input" type="datetime-local" value={editForm.follow_up_at} onChange={(e) => setEditForm((f) => ({ ...f, follow_up_at: e.target.value }))} />
              </div>
              {lead.status === "perdido" && (
                <div className="finModal-section">
                  <div className="finModal-fieldLabel">Motivo da perda</div>
                  <input className="finModal-input" value={editForm.loss_reason} onChange={(e) => setEditForm((f) => ({ ...f, loss_reason: e.target.value }))} placeholder="Ex: Preço, prazo, concorrência..." />
                </div>
              )}
              <div className="finModal-section">
                <div className="finModal-fieldLabel">Mover etapa</div>
                <select className="finModal-select" value={lead.stage_id || ""} onChange={(e) => moveLead(e.target.value)}>
                  <option value="">Sem etapa</option>
                  {stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>

              {/* Footer */}
              <div className="finModal-footer" style={{ flexWrap: "wrap", gap: 8 }}>
                {lead.status !== "aberto"  && <button type="button" className="btn btn-ghost btn-sm" onClick={() => changeStatus("aberto")}>↩ Reabrir</button>}
                {setActivePage && <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setActivePage("atendimentos"); onClose(); }}>Ver conversas</button>}
                <div style={{ flex: 1 }} />
                {lead.status !== "perdido" && <button type="button" className="btn btn-danger btn-sm" onClick={() => changeStatus("perdido")}>❌ Perdido</button>}
                {lead.status !== "ganho"   && <button type="button" className="btn btn-success btn-sm" onClick={() => changeStatus("ganho")}>✅ Ganho</button>}
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? "Salvando…" : "Salvar"}</button>
              </div>
            </form>
          )}

          {tab === "tasks" && (
            <div className="tasksList">
              <div className="tasksHeader">
                <strong>{tasks.length} tarefa(s) · {tasks.filter((t) => !t.done).length} pendente(s)</strong>
                <button className="btn btn-secondary btn-sm" onClick={() => setAddingTask((v) => !v)}><Icon name="plus" size={13} /> Nova tarefa</button>
              </div>
              {addingTask && (
                <form onSubmit={createTask} className="taskAddForm">
                  <input className="finModal-input" value={taskForm.title} onChange={(e) => setTaskForm((f) => ({ ...f, title: e.target.value }))} placeholder="Título da tarefa" required />
                  <input className="finModal-input" value={taskForm.description} onChange={(e) => setTaskForm((f) => ({ ...f, description: e.target.value }))} placeholder="Descrição (opcional)" />
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div className="finModal-section">
                      <div className="finModal-fieldLabel">Prazo</div>
                      <input className="finModal-input" type="datetime-local" value={taskForm.due_at} onChange={(e) => setTaskForm((f) => ({ ...f, due_at: e.target.value }))} />
                    </div>
                    <div className="finModal-section">
                      <div className="finModal-fieldLabel">Prioridade</div>
                      <select className="finModal-select" value={taskForm.priority} onChange={(e) => setTaskForm((f) => ({ ...f, priority: e.target.value }))}>
                        <option value="baixa">Baixa</option><option value="media">Média</option><option value="alta">Alta</option>
                      </select>
                    </div>
                  </div>
                  <div className="finModal-footer">
                    <button type="button" className="btn btn-ghost" onClick={() => setAddingTask(false)}>Cancelar</button>
                    <button type="submit" className="btn btn-primary">Criar tarefa</button>
                  </div>
                </form>
              )}
              {tasks.length === 0 && !addingTask && (
                <div className="emptyTasks"><Icon name="task" size={24} /><p>Nenhuma tarefa criada para este lead.</p></div>
              )}
              {tasks.map((task) => <TaskItem key={task.id} task={task} onToggle={toggleTask} onDelete={deleteTask} />)}
            </div>
          )}

          {tab === "notes" && (
            <form onSubmit={saveInfo} className="leadEditForm">
              <div className="finModal-section">
                <div className="finModal-fieldLabel">Anotações internas</div>
                <textarea className="finModal-input" rows={8} value={editForm.notes} onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))}
                  placeholder="Informações relevantes sobre o lead, histórico de negociações, preferências do cliente..." />
              </div>
              <div className="finModal-footer">
                <div style={{ flex: 1 }} />
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? "Salvando…" : "Salvar anotações"}</button>
              </div>
            </form>
          )}

        </div>
      </div>
    </div>
  );
}

/* ── Card 3-dot menu ─────────────────────────────────────────── */
function CardMenu({ lead, pos, onClose, onDetail, onStatus, menuRef }) {
  return (
    <div ref={menuRef} className="contactRowMenu" style={{ position: "fixed", top: pos.y, left: pos.x, zIndex: 9999, minWidth: 170 }}>
      <button type="button" className="contactRowMenuItem" onClick={() => { onClose(); onDetail("info"); }}>
        <Icon name="edit" size={13} /> Editar detalhes
      </button>
      <button type="button" className="contactRowMenuItem" onClick={() => { onClose(); onDetail("tasks"); }}>
        <Icon name="task" size={13} /> Ver tarefas
      </button>
      <div className="contactRowMenuDivider" />
      {lead.status !== "ganho" && (
        <button type="button" className="contactRowMenuItem" onClick={() => { onClose(); onStatus("ganho"); }}>
          <Icon name="checkCircle" size={13} /> Marcar como ganho
        </button>
      )}
      {lead.status !== "perdido" && (
        <button type="button" className="contactRowMenuItem danger" onClick={() => { onClose(); onStatus("perdido"); }}>
          <Icon name="x" size={13} /> Marcar como perdido
        </button>
      )}
      {lead.status !== "aberto" && (
        <button type="button" className="contactRowMenuItem" onClick={() => { onClose(); onStatus("aberto"); }}>
          <Icon name="refresh" size={13} /> Reabrir
        </button>
      )}
    </div>
  );
}

/* ── Main page ───────────────────────────────────────────────── */
function FunnelPage({ api, user, setActivePage }) {
  const [stages, setStages]   = useState([]);
  const [leads, setLeads]     = useState([]);
  const [contacts, setContacts] = useState([]);
  const [users, setUsers]     = useState([]);
  const [notice, setNotice]   = useState("");

  // Kanban state
  const [draggingLeadId, setDraggingLeadId] = useState(null);
  const [dropStageId, setDropStageId]       = useState(null);

  // Filter state
  const [leadSearch, setLeadSearch]             = useState("");
  const [responsibleFilter, setResponsibleFilter] = useState("all");
  const [statusFilter, setStatusFilter]           = useState("all");

  // UI modals / panels
  const [activeLead, setActiveLead]           = useState(null);  // id — opens LeadDrawer
  const [drawerTab, setDrawerTab]             = useState("info");
  const [newLeadModal, setNewLeadModal]       = useState(null);  // null | { defaultStageId }
  const [pendenciasModal, setPendenciasModal] = useState(null);  // lead | null
  const [anexosModal, setAnexosModal]         = useState(null);  // lead | null

  // Card 3-dot menu
  const [cardMenu, setCardMenu] = useState(null); // { lead, x, y }
  const cardMenuRef = useRef(null);

  useEffect(() => {
    if (!cardMenu) return;
    function onDoc(e) { if (cardMenuRef.current && !cardMenuRef.current.contains(e.target)) setCardMenu(null); }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [cardMenu]);

  /* ── Data ──────────────────────────────────────────────────── */
  async function loadAll() {
    const [sR, lR, cR, uR] = await Promise.all([
      api.get("/funnel/stages"), api.get("/leads"),
      api.get("/contacts"), api.get("/users"),
    ]);
    setStages(sR.data); setLeads(lR.data); setContacts(cR.data); setUsers(uR.data);
  }
  useEffect(() => { loadAll(); }, []);

  /* ── Actions ───────────────────────────────────────────────── */
  async function moveLead(lead, stageId) {
    const nextId = Number(stageId);
    setLeads((cur) => cur.map((l) => l.id === lead.id ? { ...l, stage_id: nextId } : l));
    try {
      await api.patch(`/leads/${lead.id}/stage`, { stage_id: nextId });
      setNotice(`Movido para ${stages.find((s) => s.id === nextId)?.name || "nova etapa"}.`);
      await loadAll();
    } catch (err) {
      setNotice(err.response?.data?.error || "Erro ao mover lead."); await loadAll();
    } finally { setDraggingLeadId(null); setDropStageId(null); }
  }

  async function changeLeadStatus(lead, status) {
    try { await api.patch(`/leads/${lead.id}/status`, { status }); await loadAll(); }
    catch (err) { setNotice(err.response?.data?.error || "Erro ao alterar status."); }
  }

  async function openConversationForLead(lead) {
    if (!lead.contact_id) { setNotice("Este lead não possui contato vinculado para abrir conversa."); return; }
    try {
      const res = await api.post("/conversations", { contact_id: lead.contact_id, status: "novo" });
      const convId = res.data?.id || res.data?.conversation_id;
      if (setActivePage && convId) setActivePage("atendimentos", { conversationId: convId });
      else setNotice("Conversa criada — acesse o menu Conversas.");
    } catch (err) { setNotice(err.response?.data?.error || "Erro ao abrir conversa."); }
  }

  function openCardMenu(lead, e) {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setCardMenu({ lead, x: rect.right - 175, y: rect.bottom + 4 });
  }

  function openDetailDrawer(leadId, tab = "info") {
    setActiveLead(leadId); setDrawerTab(tab);
  }

  /* ── KPIs ──────────────────────────────────────────────────── */
  function stageTotal(stageId) {
    return leads.filter((l) => l.stage_id === stageId).reduce((s, l) => s + Number(l.value || 0), 0);
  }

  const filteredLeads = useMemo(() => {
    const term = leadSearch.trim().toLowerCase();
    return leads.filter((lead) => {
      if (statusFilter !== "all" && lead.status !== statusFilter) return false;
      if (responsibleFilter === "mine" && Number(lead.responsible_user_id || 0) !== Number(user.id)) return false;
      if (responsibleFilter === "unassigned" && lead.responsible_user_id) return false;
      if (!["all", "mine", "unassigned"].includes(responsibleFilter) && String(lead.responsible_user_id || "") !== responsibleFilter) return false;
      if (!term) return true;
      return [lead.title, lead.contact_name, lead.contact_phone, lead.responsible_name].filter(Boolean).join(" ").toLowerCase().includes(term);
    });
  }, [leadSearch, leads, responsibleFilter, statusFilter, user.id]);

  const openLeads  = filteredLeads.filter((l) => !["ganho", "perdido"].includes(l.status));
  const wonLeads   = leads.filter((l) => l.status === "ganho");
  const lostLeads  = leads.filter((l) => l.status === "perdido");
  const pipelineVal = openLeads.reduce((s, l) => s + Number(l.value || 0), 0);
  const wonVal      = wonLeads.reduce((s, l) => s + Number(l.value || 0), 0);
  const convRate    = leads.length > 0 ? Math.round((wonLeads.length / leads.length) * 100) : 0;
  const ticketMedio = wonLeads.length > 0 ? wonVal / wonLeads.length : 0;

  const selectedLead = activeLead ? leads.find((l) => Number(l.id) === Number(activeLead)) || null : null;

  function onDropLead(stageId) {
    if (!draggingLeadId) return;
    const dragged = leads.find((l) => l.id === draggingLeadId);
    if (!dragged || Number(dragged.stage_id) === Number(stageId)) { setDraggingLeadId(null); setDropStageId(null); return; }
    moveLead(dragged, stageId);
  }

  /* ── Render ────────────────────────────────────────────────── */
  return (
    <>
      {/* ── Modals ── */}
      {newLeadModal && (
        <NewLeadModal
          contacts={contacts} stages={stages} users={users}
          api={api} user={user}
          defaultStageId={newLeadModal.defaultStageId}
          onClose={() => setNewLeadModal(null)}
          onSaved={() => { setNewLeadModal(null); loadAll(); setNotice("Lead criado com sucesso!"); }}
        />
      )}
      {pendenciasModal && (
        <PendenciasModal lead={pendenciasModal} api={api} onClose={() => setPendenciasModal(null)} />
      )}
      {anexosModal && (
        <AnexosModal lead={anexosModal} api={api}
          onClose={() => setAnexosModal(null)}
          onSaved={() => loadAll()}
        />
      )}
      {cardMenu && (
        <CardMenu
          lead={cardMenu.lead}
          pos={{ x: cardMenu.x, y: cardMenu.y }}
          menuRef={cardMenuRef}
          onClose={() => setCardMenu(null)}
          onDetail={(tab) => { openDetailDrawer(cardMenu.lead.id, tab); }}
          onStatus={(status) => changeLeadStatus(cardMenu.lead, status)}
        />
      )}

      {/* ── Page wrapper ── */}
      <div className="funnelPage">
        <div className="funnelPageHeader">
        {/* Header */}
        <div className="page-header-v2">
          <div>
            <div className="page-title-v2">Lead</div>
            <div className="page-subtitle-v2">Pipeline de vendas · Kanban com drag &amp; drop</div>
          </div>
          <div className="page-actions-v2">
            <div className="funnelViewToggle">
              <button type="button" className="funnelViewBtn active"><Icon name="grid" size={13} /> Kanban</button>
              <button type="button" className="funnelViewBtn"><Icon name="sort" size={13} /> Lista</button>
            </div>
            <button type="button" className="btn btn-secondary"><Icon name="filter" size={13} /> Filtros</button>
            <button type="button" className="btn btn-primary" onClick={() => setNewLeadModal({ defaultStageId: stages[0]?.id })}>
              <Icon name="plus" size={13} /> Novo lead
            </button>
          </div>
        </div>

        {/* KPI strip */}
        <div className="funnel-kpi-strip">
          <div className="metric">
            <div className="metric-label">
              <span className="metric-icon" style={{ background: "var(--info-bg)", borderColor: "var(--info-border)", color: "var(--info)" }}><Icon name="deal" size={11} /></span>
              Pipeline ativo
            </div>
            <div className="metric-value">{openLeads.length}</div>
            <div className="metric-sub">{formatMoney(pipelineVal)} em negociação</div>
          </div>
          <div className="metric">
            <div className="metric-label">
              <span className="metric-icon" style={{ background: "var(--success-bg)", borderColor: "var(--success-border)", color: "var(--success)" }}><Icon name="check" size={11} /></span>
              Ganhos
            </div>
            <div className="metric-value">{wonLeads.length}</div>
            <div className="metric-sub">{wonLeads.length === 1 ? "negócio fechado" : "negócios fechados"}</div>
          </div>
          <div className="metric">
            <div className="metric-label">
              <span className="metric-icon" style={{ background: "var(--danger-bg)", borderColor: "var(--danger-border)", color: "var(--danger)" }}><Icon name="x" size={11} /></span>
              Perdas
            </div>
            <div className="metric-value">{lostLeads.length}</div>
            <div className="metric-sub">sem conversão</div>
          </div>
          <div className="metric">
            <div className="metric-label">
              <span className="metric-icon" style={{ background: "rgba(37,211,102,.10)", borderColor: "rgba(37,211,102,.25)", color: "var(--brand-500)" }}><Icon name="trendUp" size={11} /></span>
              Conversão
            </div>
            <div className="metric-value">{convRate}<span className="metric-value-unit">%</span></div>
            <div className="metric-sub">ganhos / total</div>
          </div>
          <div className="metric">
            <div className="metric-label">
              <span className="metric-icon" style={{ background: "var(--purple-bg)", borderColor: "rgba(167,139,250,.25)", color: "var(--purple)" }}><Icon name="money" size={11} /></span>
              Ticket médio
            </div>
            <div className="metric-value">{formatMoney(ticketMedio)}</div>
            <div className="metric-sub">por negócio ganho</div>
          </div>
        </div>

        {/* Filter bar */}
        <div className="funnelFilterBar">
          <div className="funnelSearchWrap">
            <Icon name="search" size={13} style={{ color: "var(--text-4)", flexShrink: 0 }} />
            <input value={leadSearch} onChange={(e) => setLeadSearch(e.target.value)} placeholder="Buscar por lead, cliente, telefone..." />
          </div>
          <select value={responsibleFilter} onChange={(e) => setResponsibleFilter(e.target.value)} className="funnelSelect">
            <option value="all">Todos os responsáveis</option>
            <option value="mine">Meus leads</option>
            <option value="unassigned">Sem responsável</option>
            {users.map((u) => <option key={u.id} value={String(u.id)}>{u.name}</option>)}
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="funnelSelect">
            <option value="all">Todos os status</option>
            <option value="aberto">Abertos</option>
            <option value="ganho">Ganhos</option>
            <option value="perdido">Perdidos</option>
          </select>
          {(leadSearch || responsibleFilter !== "all" || statusFilter !== "all") && (
            <button type="button" className="funnelClearBtn" onClick={() => { setLeadSearch(""); setResponsibleFilter("all"); setStatusFilter("all"); }}>
              Limpar filtros
            </button>
          )}
        </div>

        {notice && (
          <div className="noticeBox" style={{ margin: "0 0 10px" }}>
            {notice}
            <button type="button" style={{ marginLeft: 8, background: "none", border: "none", cursor: "pointer", color: "inherit" }} onClick={() => setNotice("")}>×</button>
          </div>
        )}
        </div>{/* /funnelPageHeader */}

        {/* Kanban board */}
        <section className="kanbanBoard">
          {stages.map((stage, stageIdx) => {
            const stageLeads  = filteredLeads.filter((l) => l.stage_id === stage.id);
            const dotColor    = STAGE_COLORS[stageIdx % STAGE_COLORS.length];
            const isDropReady = dropStageId === stage.id;

            return (
              <div
                key={stage.id}
                className={`kanbanColumn${isDropReady ? " dropReady" : ""}`}
                style={isDropReady ? { "--drop-color": dotColor } : { "--drop-color": "transparent" }}
                onDragOver={(e) => { e.preventDefault(); setDropStageId(stage.id); }}
                onDragEnter={() => setDropStageId(stage.id)}
                onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDropStageId(null); }}
                onDrop={(e) => { e.preventDefault(); onDropLead(stage.id); }}
              >
                {/* Column header */}
                <div className="kanbanColHead">
                  <div className="kanbanColLeft">
                    <span className="kanbanDot" style={{ background: dotColor }} />
                    <strong>{stage.name}</strong>
                    <em className="kanbanCount">{stageLeads.length}</em>
                  </div>
                  <button type="button" className="kanbanAddBtn" title="Adicionar lead nesta etapa"
                    onClick={() => setNewLeadModal({ defaultStageId: stage.id })}>
                    <Icon name="plus" size={13} />
                  </button>
                </div>
                <div className="kanbanColAmount">{formatMoney(stageTotal(stage.id))}</div>

                {/* Cards scroll area */}
                <div className="kanbanCards">
                  {stageLeads.length === 0 && (
                    <div className={`kanbanEmpty${isDropReady ? " dropActive" : ""}`}>
                      {isDropReady ? "Solte aqui para mover" : "Arraste um lead para esta etapa"}
                    </div>
                  )}
                  {stageLeads.map((lead) => {
                    const originLabel = ORIGIN_LABELS[lead.origin] || null;
                    const age         = timeSince(lead.created_at);
                    return (
                      <div
                        key={lead.id}
                        className={`leadCard lead-${lead.status}${draggingLeadId === lead.id ? " dragging" : ""}${Number(activeLead) === Number(lead.id) ? " selected" : ""}`}
                        style={{ borderLeftColor: dotColor, cursor: "pointer" }}
                        draggable
                        onDragStart={() => setDraggingLeadId(lead.id)}
                        onDragEnd={() => { setDraggingLeadId(null); setDropStageId(null); }}
                        onClick={() => openDetailDrawer(lead.id, "info")}
                      >
                        {/* Card header */}
                        <div className="lcHead">
                          <span className="lcTitle">
                            {lead.title || lead.contact_name || "Oportunidade"}
                          </span>
                          <button type="button" className="lcMenuBtn" onClick={(e) => { e.stopPropagation(); openCardMenu(lead, e); }}>
                            <Icon name="moreV" size={14} />
                          </button>
                        </div>

                        {/* Origin + age */}
                        <div className="lcOrigin">
                          {originLabel && <><span>{originLabel}</span><span className="lcDot">·</span></>}
                          <span>{age}</span>
                        </div>

                        {/* Value + responsible avatar */}
                        <div className="lcValueRow">
                          <span className="lcValue" style={{ color: dotColor }}>{formatMoney(lead.value)}</span>
                          <Avatar name={lead.responsible_name || lead.contact_name || "?"} size="xs" />
                        </div>

                        {/* Follow-up chip */}
                        {lead.follow_up_at && (
                          <div className={`leadFollowUp${new Date(lead.follow_up_at) < new Date() ? " overdue" : ""}`}>
                            <Icon name="clock" size={10} /> {formatTime(lead.follow_up_at)}
                          </div>
                        )}

                        {/* Footer action links */}
                        <div className="lcFooter">
                          <button type="button" className="lcFooterBtn" onClick={(e) => { e.stopPropagation(); openConversationForLead(lead); }}>
                            <Icon name="message" size={11} /> Conversa
                          </button>
                          <span className="lcFooterSep" />
                          <button type="button" className="lcFooterBtn" onClick={(e) => { e.stopPropagation(); setAnexosModal(lead); }}>
                            <Icon name="attach" size={11} /> Anexos
                          </button>
                          <span className="lcFooterSep" />
                          <button type="button" className="lcFooterBtn" onClick={(e) => { e.stopPropagation(); setPendenciasModal(lead); }}>
                            <Icon name="task" size={11} /> Pendência
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </section>
      </div>

      {/* Lead modal */}
      {selectedLead && (
        <LeadDrawer
          lead={selectedLead} stages={stages} users={users}
          api={api} user={user}
          onClose={() => setActiveLead(null)}
          onReload={loadAll}
          setActivePage={setActivePage}
          initialTab={drawerTab}
        />
      )}
    </>
  );
}

export default FunnelPage;
